import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, type ChangeEvent, type FC, type MouseEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { getUploadCSVTemplate, resetUploadCSVTemplate } from 'src/redux/actions/patientAction';
import SimpleSnackbar from './details-page/sub-components/snackBar';
import { format } from 'date-fns';

interface PatientUploadTableProps {
  count?: number;
  items?: any;
  onPageChange?: (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  onRowsPerPageChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onRowClick?: (event: ChangeEvent<HTMLInputElement>) => void;
  page?: number;
  rowsPerPage?: number;
  source: string;
  validateCSVResponse?: any;
}
const PatientUploadTable: FC<PatientUploadTableProps> = (props) => {
  const {
    count = 0,
    items = [],
    onPageChange = () => {},
    onRowsPerPageChange,
    onRowClick = () => {},
    page = 0,
    rowsPerPage = 10,
    source = '',
    validateCSVResponse = [],
  } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [patientError, setPatientError] = useState('');
  const uploadCSVResponse = useSelector((state: any) => state.uploadCsvTemplateReducer);

  const handleUpload = () => {
    dispatch(getUploadCSVTemplate(validateCSVResponse));
  };

  useEffect(() => {
    if (!uploadCSVResponse.isUploadSuccess && uploadCSVResponse.response === null) return;
    if (!uploadCSVResponse.isUploadSuccess && uploadCSVResponse.response !== null) {
      setPatientError(uploadCSVResponse?.response?.message);
      return;
    }
    if (uploadCSVResponse?.isUploadSuccess) setPatientError('');
    setTimeout(() => {
      dispatch(resetUploadCSVTemplate());
      navigate(-1);
    }, 3000);
    return;
  }, [uploadCSVResponse]);

  return (
    <Box sx={{ position: 'relative' }}>
      {source !== 'invalidRecords' && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 4 }}>
          <Button
            variant="contained"
            disabled={items[0]?.patientSuccess ? false : true}
            onClick={handleUpload}
          >
            Confirm Import
          </Button>
        </Box>
      )}

      <Table sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow>
            {source === 'invalidRecords' && <TableCell>Message</TableCell>}

            <TableCell>First Name</TableCell>
            <TableCell>Middle Name</TableCell>
            <TableCell>Last Name</TableCell>
            <TableCell>Gender</TableCell>
            <TableCell>Date of Birth</TableCell>
            <TableCell>Bed Assignment</TableCell>
            <TableCell>Medical Record Number</TableCell>
            <TableCell>Social Security Number</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items?.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9}>
                <Box
                  textAlign="center"
                  p={3}
                >
                  <Typography
                    color={'text.secondary'}
                    variant="subtitle2"
                  >
                    No Records Found
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            items?.map((patient: any) => {
              const patientSuccess = patient?.patientSuccess
                ? patient?.patientSuccess
                : patient?.importPatientRequestDto;
              return (
                <TableRow key={patientSuccess?.bedAssignment}>
                  {source === 'invalidRecords' && (
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={'red'}
                      >
                        {patient?.message ? patient?.message : '-'}
                      </Typography>
                    </TableCell>
                  )}

                  <TableCell>
                    {patientSuccess?.firstName ? patientSuccess?.firstName : '-'}
                  </TableCell>

                  <TableCell>
                    {patientSuccess?.middleName ? patientSuccess?.middleName : '-'}
                  </TableCell>
                  <TableCell>{patientSuccess?.lastName ? patientSuccess?.lastName : '-'}</TableCell>

                  <TableCell>{patientSuccess?.gender ? patientSuccess?.gender : '-'}</TableCell>
                  <TableCell>
                    {patientSuccess?.dateOfBirth
                      ? format(new Date(patientSuccess?.dateOfBirth), 'MMM dd, yyyy')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {patientSuccess?.bedAssignment ? patientSuccess?.bedAssignment : '-'}
                  </TableCell>
                  <TableCell>
                    {patientSuccess?.medicalRecordNumber
                      ? patientSuccess?.medicalRecordNumber
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {patientSuccess?.socialSecurityNumber
                      ? patientSuccess?.socialSecurityNumber
                          ?.toString()
                          ?.replace(/^(\d{5})(\d{4})$/, 'XXX-XX-$2')
                      : '-'}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={count}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />
      {uploadCSVResponse.isUploadSuccess && (
        <SimpleSnackbar
          message={
            uploadCSVResponse.isUploadSuccess
              ? 'Data is imported Successfully'
              : 'Patient details are imported successfully!'
          }
        />
      )}
    </Box>
  );
};

export default PatientUploadTable;
